import i18next from 'i18next'
import { initReactI18next } from 'react-i18next'

import de from './locales/de.json'
import en from './locales/en.json'
import fr from './locales/fr.json'

/**
 * Langue de l'INTERFACE (boutons, libellés, états).
 *
 * Le contenu métier — nom de l'événement, slogan, FAQ, blocs éditoriaux — ne
 * passe jamais par ici : il est résolu par le backend selon `Accept-Language`
 * et arrive déjà traduit. Deux mécanismes distincts, pour deux natures de
 * texte : l'un se déploie, l'autre s'administre.
 */

export const SUPPORTED_LOCALES = ['de', 'fr', 'en'] as const
export type Locale = (typeof SUPPORTED_LOCALES)[number]

const STORAGE_KEY = 'masked-night.locale'
export const FALLBACK_LOCALE: Locale = 'de'

/** Le paramètre d'URL qui impose une langue : `?lang=en`. */
export const LOCALE_PARAM = 'lang'

export function isLocale(value: string | null | undefined): value is Locale {
  return Boolean(value && (SUPPORTED_LOCALES as readonly string[]).includes(value))
}

// Le stockage peut être refusé (navigation privée, données de site bloquées) :
// la langue doit alors rester utilisable pour la visite en cours, sans erreur.
function readStored(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

function writeStored(locale: Locale): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale)
  } catch {
    // Rien à faire : le choix vaut pour cette page, pas pour la suivante.
  }
}

/**
 * `?lang=xx` dans l'adresse, lu UNE fois au chargement.
 *
 * Un QR collé sur une table anglophone, le lien d'un e-mail, la télé de la
 * salle : chacun peut imposer sa langue sans qu'on ait à toucher l'appareil.
 * Le choix est enregistré comme si on avait cliqué sur le sélecteur, puis le
 * paramètre est retiré de l'adresse (`replaceState`, sans recharger) : sinon
 * un lien copié plus tard réimposerait cette langue à quelqu'un d'autre, et
 * un rechargement écraserait le choix fait entre-temps au sélecteur.
 */
export function consumeLocaleParam(): Locale | null {
  if (typeof window === 'undefined') return null
  const url = new URL(window.location.href)
  if (!url.searchParams.has(LOCALE_PARAM)) return null

  const requested = url.searchParams.get(LOCALE_PARAM)?.trim().toLowerCase().split('-')[0]
  url.searchParams.delete(LOCALE_PARAM)
  // L'état d'historique est conservé : le routeur y range sa clé de page.
  window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash)

  if (!isLocale(requested)) return null
  writeStored(requested)
  return requested
}

export function detectLocale(): Locale {
  const stored = readStored()
  if (isLocale(stored)) return stored
  for (const language of navigator.languages ?? [navigator.language]) {
    const base = language.split('-')[0]
    if (isLocale(base)) return base
  }
  return FALLBACK_LOCALE
}

/** Vrai si une langue a été CHOISIE sur cet appareil (et pas seulement devinée). */
export function hasChosenLocale(): boolean {
  return isLocale(readStored())
}

export async function setLocale(locale: Locale): Promise<void> {
  writeStored(locale)
  document.documentElement.lang = locale
  await i18next.changeLanguage(locale)
}

void i18next.use(initReactI18next).init({
  resources: {
    de: { translation: de },
    fr: { translation: fr },
    en: { translation: en },
  },
  // Le paramètre d'URL l'emporte sur tout : c'est un choix explicite.
  lng: consumeLocaleParam() ?? detectLocale(),
  fallbackLng: FALLBACK_LOCALE,
  interpolation: { escapeValue: false },
})

document.documentElement.lang = i18next.language

export default i18next
